/**
 * PaySafeGo Board Report — branded deck (from docs/reference/PaySafeGo_BoardReport_Template.js).
 * All content comes from a JSON payload; layout, branding, and pagination stay in the template.
 */
import PptxGenJS from 'pptxgenjs'

const TEAL = '0A5169'
const YELLOW = 'FDE300'
const WHITE = 'FFFFFF'
const BODY = '555555'
const MUTED = '6B7B85'
const DARK = '333333'
const CARDBG = 'F7F9FA'
const CARDLN = 'E2E8EC'
const ICE = 'CADCE3'
const GREEN = '22C55E'
const AMBER = 'E0A100'
const AMBER_L = 'FBBF24'
const ORANGE = 'F97316'
const RED = 'EF4444'
const GREEN_T = 'E8F8EE'
const AMBER_T = 'FFFBE6'
const RED_T = 'FDECEC'
const F = 'Poppins'

const TONE = {
  ok: { main: GREEN, tint: GREEN_T, edge: GREEN, text: WHITE },
  green: { main: GREEN, tint: GREEN_T, edge: GREEN, text: WHITE },
  amber: { main: AMBER, tint: AMBER_T, edge: AMBER, text: DARK },
  working: { main: AMBER, tint: AMBER_T, edge: AMBER, text: DARK },
  orange: { main: ORANGE, tint: 'FEF0E6', edge: ORANGE, text: WHITE },
  red: { main: RED, tint: RED_T, edge: RED, text: WHITE },
  questions: { main: RED, tint: RED_T, edge: RED, text: WHITE },
  neutral: { main: TEAL, tint: CARDBG, edge: YELLOW, text: WHITE },
}
const tone = (t) => TONE[t] || TONE.neutral
const pillFill = (t) => (t === 'amber' || t === 'working' ? AMBER_L : tone(t).main)
const pillText = (t) => (t === 'amber' || t === 'working' ? DARK : WHITE)

const PAGE_W = 10
const PAGE_H = 5.625
const MARGIN = 0.5
const FOOTER_Y = 5.18
const CONTENT_BOTTOM = 5.05
const ROW_H = 0.35
const LABEL_H = 0.30
const STAT_H = 1.02
const STAT_Y = 1.28
const BODY_TOP = 1.28

export async function exportFormattedBoardReport(D, {
  filename = 'PaySafeGo_BoardReport.pptx',
} = {}) {
  if (!D?.meta?.period) throw new Error('Formatted report needs a period')

  const DECKNAME = `${D.meta.brand || 'PaySafeGo'} · ${D.meta.deckName || (`Board AML/CTF Report — ${D.meta.period}`)}`
  const plan = []
  const planSlide = (fn) => { plan.push(fn) }
  let pres

  function wordmark(s, onTeal) {
    s.addText([
      { text: 'PaySafe', options: { color: onTeal ? WHITE : TEAL, bold: true } },
      { text: 'Go', options: { color: YELLOW, bold: true } },
    ], { x: 7.7, y: 0.25, w: 2.0, h: 0.35, fontSize: 15, align: 'right', fontFace: F })
  }
  function footer(s, page, total) {
    s.addShape(pres.ShapeType.line, { x: MARGIN, y: FOOTER_Y, w: 9, h: 0, line: { color: 'DDDDDD', width: 0.75 } })
    s.addText(DECKNAME, { x: MARGIN, y: 5.22, w: 6, h: 0.28, fontSize: 8, color: MUTED, fontFace: F })
    s.addText(`Confidential — Board   ·   ${page} / ${total}`, { x: 6.0, y: 5.22, w: 3.5, h: 0.28, fontSize: 8, color: MUTED, align: 'right', fontFace: F })
  }
  function titleBlock(s, title, subtitle) {
    s.addShape(pres.ShapeType.rect, { x: MARGIN, y: 0.52, w: 0.22, h: 0.22, fill: { color: YELLOW }, line: { color: YELLOW } })
    s.addText(title, { x: 0.85, y: 0.38, w: 6.7, h: 0.5, fontSize: 25, bold: true, color: TEAL, fontFace: F, margin: 0 })
    if (subtitle) s.addText(subtitle, { x: 0.86, y: 0.86, w: 8.6, h: 0.3, fontSize: 12, italic: true, color: MUTED, fontFace: F, margin: 0 })
  }
  function statTile(s, x, y, w, k) {
    const t = tone(k.status || 'neutral')
    const numColor = (k.status && k.status !== 'neutral') ? t.main : TEAL
    s.addShape(pres.ShapeType.rect, { x, y, w, h: STAT_H, fill: { color: CARDBG }, line: { color: CARDLN, width: 0.75 } })
    s.addShape(pres.ShapeType.rect, { x, y, w, h: 0.05, fill: { color: t.edge }, line: { color: t.edge } })
    s.addText(String(k.value), { x: x + 0.12, y: y + 0.12, w: w - 0.24, h: 0.44, fontSize: 24, bold: true, color: numColor, fontFace: F, margin: 0 })
    s.addText(String(k.label).toUpperCase(), { x: x + 0.12, y: y + 0.56, w: w - 0.24, h: 0.2, fontSize: 7.5, bold: true, color: MUTED, charSpacing: 1, fontFace: F, margin: 0 })
    if (k.sub) s.addText(k.sub, { x: x + 0.12, y: y + 0.74, w: w - 0.24, h: 0.24, fontSize: 8, color: BODY, fontFace: F, margin: 0 })
  }
  function statRow(s, stats) {
    const n = Math.min(stats.length, 5)
    const gap = 0.14
    const w = (9.0 - gap * (n - 1)) / n
    stats.slice(0, 5).forEach((k, i) => statTile(s, MARGIN + i * (w + gap), STAT_Y, w, k))
  }
  function pill(s, x, y, w, text, t) {
    s.addShape(pres.ShapeType.roundRect, { x, y, w, h: 0.24, rectRadius: 0.12, fill: { color: pillFill(t) }, line: { color: pillFill(t) } })
    s.addText(text, { x, y: y - 0.005, w, h: 0.25, fontSize: 8, bold: true, color: pillText(t), align: 'center', fontFace: F, margin: 0 })
  }
  function dot(s, x, y, t) {
    s.addShape(pres.ShapeType.ellipse, { x, y, w: 0.12, h: 0.12, fill: { color: tone(t).main }, line: { color: tone(t).main } })
  }
  function sectionLabel(s, x, y, w, text) {
    s.addText(String(text).toUpperCase(), { x, y, w, h: 0.24, fontSize: 10, bold: true, color: TEAL, charSpacing: 2, fontFace: F, margin: 0 })
  }
  function calloutBand(s, x, y, w, c) {
    const t = tone(c.tone || 'red')
    s.addShape(pres.ShapeType.rect, { x, y, w, h: 0.62, fill: { color: t.tint }, line: { color: t.main, width: 0.75 } })
    let tx = x + 0.18
    if (c.kicker) {
      const kw = Math.max(0.55, String(c.kicker).length * 0.11 + 0.15)
      s.addText(String(c.kicker).toUpperCase(), { x: tx, y: y + 0.08, w: kw, h: 0.2, fontSize: 9, bold: true, color: t.main, charSpacing: 2, fontFace: F, margin: 0 })
      tx += kw + 0.08
    }
    s.addText(c.text, { x: tx, y: y + 0.03, w: x + w - tx - 0.15, h: 0.56, fontSize: 9, color: DARK, fontFace: F, valign: 'middle', margin: 0 })
  }
  function noteCard(s, x, y, w, h, n) {
    s.addShape(pres.ShapeType.rect, { x, y, w, h, fill: { color: CARDBG }, line: { color: CARDLN, width: 0.75 } })
    s.addShape(pres.ShapeType.rect, { x, y, w, h: 0.05, fill: { color: YELLOW }, line: { color: YELLOW } })
    s.addText(String(n.kicker || 'NOTE').toUpperCase(), { x: x + 0.2, y: y + 0.16, w: w - 0.4, h: 0.22, fontSize: 9, bold: true, color: TEAL, charSpacing: 2, fontFace: F, margin: 0 })
    const paras = Array.isArray(n.body) ? n.body : [n.body]
    s.addText(paras.map((p, i) => ({ text: p, options: i < paras.length - 1 ? { breakLine: true, paraSpaceAfter: 8 } : {} })), {
      x: x + 0.2, y: y + 0.44, w: w - 0.4, h: h - 0.6, fontSize: 9.5, color: BODY, fontFace: F, valign: 'top', margin: 0,
    })
  }
  function tableRows(s, x, y, w, cols, rows, hasPill) {
    const pillLeft = hasPill ? x + w - 1.14 : x + w
    rows.forEach((r, i) => {
      const ry = y + i * ROW_H
      s.addShape(pres.ShapeType.rect, { x, y: ry, w, h: 0.30, fill: { color: i % 2 ? WHITE : CARDBG }, line: { color: CARDLN, width: 0.5 } })
      let cx = x + 0.12
      if (r.dot) { dot(s, cx, ry + 0.09, r.dot); cx += 0.24 }
      cols.forEach((c, j) => {
        const val = r.cells[j] != null ? String(r.cells[j]) : ''
        const maxW = Math.max(0, pillLeft - 0.06 - cx)
        const cw = Math.min(c.w, maxW)
        if (cw >= 0.18) {
          s.addText(val, {
            x: cx, y: ry + 0.02, w: cw, h: 0.26, fontSize: 8.5,
            bold: !!c.bold, color: c.muted ? MUTED : (c.bold ? DARK : BODY),
            fontFace: c.mono ? 'Courier New' : F, align: c.align || 'left', valign: 'middle',
            margin: 0, wrap: false,
          })
        }
        cx += c.w + 0.06
      })
      if (hasPill && r.pill) pill(s, x + w - 1.12, ry + 0.035, 1.0, r.pill.text, r.pill.tone || 'neutral')
    })
  }
  function rowsThatFit(startY) { return Math.max(1, Math.floor((CONTENT_BOTTOM - startY) / ROW_H)) }

  function addBarChart(s, x, y, w, h, chart) {
    let items = chart.items.slice()
    if (items.length > 7) {
      const top = items.slice(0, 6)
      const rest = items.slice(6).reduce((a, b) => a + (+b.value || 0), 0)
      items = top.concat([{ label: 'Other', value: rest }])
    }
    const maxV = Math.max(...items.map((i) => +i.value || 0), 1)
    s.addChart(pres.ChartType.bar, [{
      name: chart.series || 'Series', labels: items.map((i) => i.label), values: items.map((i) => +i.value || 0),
    }], {
      x, y, w, h, barDir: 'bar',
      chartColors: [TEAL], showLegend: false, showTitle: false,
      showValue: true, dataLabelPosition: 'outEnd', dataLabelColor: DARK, dataLabelFontSize: 8.5, dataLabelFontFace: F,
      dataLabelFormatCode: chart.valueFormat || 'General',
      catAxisLabelColor: BODY, catAxisLabelFontSize: 9, catAxisLabelFontFace: F,
      valAxisHidden: true, valGridLine: { style: 'none' }, catGridLine: { style: 'none' },
      valAxisMaxVal: Math.ceil(maxV * 1.2), barGapWidthPct: 60,
    })
  }
  function addDoughnut(s, x, y, w, h, chart) {
    const toneMap = { ok: GREEN, green: GREEN, amber: AMBER_L, orange: ORANGE, red: RED, neutral: TEAL }
    const items = chart.items || []
    const side = Math.min(h, Math.max(2.05, w * 0.58), 2.55)
    const plotX = x
    const plotY = y + Math.max(0, (h - side) / 2)
    s.addChart(pres.ChartType.doughnut, [{
      name: chart.series || 'Series', labels: items.map((i) => i.label), values: items.map((i) => +i.value || 0),
    }], {
      x: plotX, y: plotY, w: side, h: side, holeSize: 70,
      chartColors: items.map((i) => toneMap[i.tone] || TEAL),
      showLegend: false, showTitle: false, showValue: false,
    })
    if (chart.centerValue) {
      const midX = plotX + side / 2
      const midY = plotY + side / 2
      const tw = Math.min(1.7, side * 0.62)
      const hasLabel = !!chart.centerLabel
      s.addText(String(chart.centerValue), {
        x: midX - tw / 2, y: midY - (hasLabel ? 0.32 : 0.20),
        w: tw, h: 0.38, fontSize: 22, bold: true, color: TEAL,
        align: 'center', valign: 'middle', fontFace: F, margin: 0,
      })
      if (hasLabel) {
        s.addText(String(chart.centerLabel).toUpperCase(), {
          x: midX - tw / 2, y: midY + 0.08,
          w: tw, h: 0.18, fontSize: 8, bold: true, color: MUTED, charSpacing: 1.2,
          align: 'center', valign: 'middle', fontFace: F, margin: 0,
        })
      }
    }
    const lx = plotX + side + 0.16
    const lw = Math.max(1.1, x + w - lx)
    items.forEach((it, i) => {
      const ly = plotY + 0.18 + i * 0.34
      const c = toneMap[it.tone] || TEAL
      s.addShape(pres.ShapeType.ellipse, { x: lx, y: ly + 0.05, w: 0.12, h: 0.12, fill: { color: c }, line: { color: c } })
      s.addText(`${it.label}  ${it.value}`, {
        x: lx + 0.2, y: ly, w: lw - 0.2, h: 0.24, fontSize: 10, color: BODY, fontFace: F, valign: 'middle', margin: 0,
      })
    })
  }
  function attentionRow(s, y, a) {
    const t = tone(a.tone || 'red')
    s.addShape(pres.ShapeType.rect, { x: MARGIN, y, w: 9.0, h: 0.64, fill: { color: t.tint }, line: { color: t.main, width: 0.75 } })
    dot(s, 0.68, y + 0.26, a.tone || 'red')
    s.addText(a.title, { x: 0.92, y: y + 0.07, w: 3.4, h: 0.5, fontSize: 10.5, bold: true, color: DARK, fontFace: F, valign: 'middle', margin: 0 })
    s.addText(a.detail || '', { x: 4.45, y: y + 0.05, w: 4.95, h: 0.56, fontSize: 8.5, color: BODY, fontFace: F, valign: 'middle', margin: 0 })
  }

  planSlide((s) => {
    s.background = { color: TEAL }
    s.addShape(pres.ShapeType.rect, { x: 9.0, y: 0.45, w: 0.55, h: 0.07, fill: { color: YELLOW }, line: { color: YELLOW } })
    s.addShape(pres.ShapeType.rect, { x: 9.48, y: 0.45, w: 0.07, h: 0.55, fill: { color: YELLOW }, line: { color: YELLOW } })
    s.addText([
      { text: 'PaySafe', options: { color: WHITE, bold: true } },
      { text: 'Go', options: { color: YELLOW, bold: true } },
    ], { x: 0.5, y: 0.35, w: 3, h: 0.45, fontSize: 24, fontFace: F })
    s.addText(('BOARD REPORT · ' + D.meta.period).toUpperCase(), { x: 0.55, y: 1.55, w: 8, h: 0.35, fontSize: 16, bold: true, color: YELLOW, charSpacing: 5, fontFace: F, margin: 0 })
    s.addText(D.meta.headline || 'AML/CTF at a glance.', { x: 0.5, y: 1.95, w: 9, h: 0.85, fontSize: 46, bold: true, color: WHITE, fontFace: F, margin: 0 })
    if (D.meta.lede) s.addText(D.meta.lede, { x: 0.55, y: 2.85, w: 7.6, h: 0.6, fontSize: 14, italic: true, color: ICE, fontFace: F, margin: 0 })
    const rag = D.meta.rag || {}
    const ragDefs = [
      { n: rag.questions, l: 'ASK QUESTIONS', c: RED },
      { n: rag.working, l: 'WORKING ON IT', c: AMBER_L },
      { n: rag.ok, l: 'ON TRACK', c: GREEN },
    ].filter((r) => r.n != null)
    ragDefs.forEach((r, i) => {
      const x = 0.55 + i * 2.35
      s.addShape(pres.ShapeType.ellipse, { x, y: 3.78, w: 0.16, h: 0.16, fill: { color: r.c }, line: { color: r.c } })
      s.addText(String(r.n), { x: x + 0.24, y: 3.62, w: 0.6, h: 0.45, fontSize: 26, bold: true, color: WHITE, fontFace: F, margin: 0 })
      s.addText(r.l, { x: x + 0.72, y: 3.76, w: 1.7, h: 0.25, fontSize: 9, bold: true, color: ICE, charSpacing: 1.5, fontFace: F, margin: 0 })
    })
    s.addShape(pres.ShapeType.rect, { x: 0.55, y: 4.55, w: 1.6, h: 0.04, fill: { color: YELLOW }, line: { color: YELLOW } })
    s.addText(('AS AT ' + D.meta.asAt + '   ·   CONFIDENTIAL — BOARD').toUpperCase(), { x: 0.55, y: 4.68, w: 8, h: 0.3, fontSize: 10, bold: true, color: YELLOW, charSpacing: 2, fontFace: F, margin: 0 })
  })

  if (D.kpis || D.attention) {
    const attn = (D.attention || []).slice()
    const perFirst = 3
    const perNext = 5
    const first = attn.splice(0, perFirst)
    planSlide((s, p, t) => {
      wordmark(s, false); footer(s, p, t)
      titleBlock(s, D.execTitle || 'The quarter in one view', D.execSubtitle || 'Green is noted, amber is managed, red is on this page for a decision.')
      if (D.kpis) statRow(s, D.kpis)
      if (first.length) {
        sectionLabel(s, MARGIN, 2.52, 5, D.attentionLabel || 'Where we need the board')
        first.forEach((a, i) => attentionRow(s, 2.82 + i * 0.74, a))
      }
    })
    while (attn.length) {
      const chunk = attn.splice(0, perNext)
      planSlide((s, p, t) => {
        wordmark(s, false); footer(s, p, t)
        titleBlock(s, (D.execTitle || 'The quarter in one view'), '(continued)')
        chunk.forEach((a, i) => attentionRow(s, BODY_TOP + i * 0.74, a))
      })
    }
  }

  function tableNeededWidth(tbl) {
    const colSum = tbl.columns.reduce((a, c) => a + c.w + 0.06, 0)
    return 0.12 + (tbl.rows.some((r) => r.dot) ? 0.24 : 0) + colSum + (tbl.hasPill ? 1.12 : 0)
  }

  ;(D.sections || []).forEach((sec) => {
    const tbl = sec.table
    const allRows = tbl ? tbl.rows.slice() : []
    const totalRows = allRows.length
    const split = !!sec.chart
    const tableX = split ? 5.25 : MARGIN
    const tableW = split ? 4.25 : 9.0
    let notePlaced = !sec.note

    const baseTop = sec.stats && sec.stats.length ? STAT_Y + STAT_H + 0.25 : BODY_TOP
    let leftTop = baseTop
    let rightTop = baseTop
    let calloutX = MARGIN
    let calloutW = 9.0
    let calloutY = null
    if (sec.callout) {
      if (split) { calloutX = 5.25; calloutW = 4.25; calloutY = rightTop; rightTop += 0.62 + 0.22 }
      else { calloutY = baseTop; leftTop = rightTop = baseTop + 0.62 + 0.17 }
    }
    const labelY = split ? rightTop : leftTop
    const chartLabelY = leftTop
    const firstRowsY = labelY + LABEL_H
    const capFirst = tbl ? rowsThatFit(firstRowsY) : 0
    const rows = allRows.slice()
    const firstChunk = rows.splice(0, capFirst)

    let noteOnFirst = false
    let noteFirstY = 0
    let noteFirstX = tableX
    let noteFirstW = tableW
    if (sec.note && tbl) {
      const usedY = firstRowsY + firstChunk.length * ROW_H + 0.15
      if (CONTENT_BOTTOM - usedY >= 1.0 && rows.length === 0) { noteOnFirst = true; noteFirstY = usedY }
    } else if (sec.note && split && !tbl) {
      noteOnFirst = true; noteFirstY = firstRowsY
    } else if (sec.note && !tbl && !sec.chart) {
      noteOnFirst = true; noteFirstY = firstRowsY; noteFirstX = MARGIN; noteFirstW = 9.0
    }
    if (noteOnFirst) notePlaced = true

    planSlide((s, p, t) => {
      wordmark(s, false); footer(s, p, t)
      titleBlock(s, sec.title, sec.subtitle)
      if (sec.stats && sec.stats.length) statRow(s, sec.stats)
      if (sec.callout) calloutBand(s, calloutX, calloutY, calloutW, sec.callout)
      if (sec.chart) {
        sectionLabel(s, MARGIN, chartLabelY, 4.6, sec.chart.label || 'Breakdown')
        const chTop = chartLabelY + LABEL_H
        const chH = Math.min(2.55, CONTENT_BOTTOM - chTop - 0.05)
        if (sec.chart.type === 'doughnut') addDoughnut(s, MARGIN, chTop, 4.55, chH, sec.chart)
        else addBarChart(s, 0.4, chTop, 4.5, chH, sec.chart)
      }
      if (tbl) {
        const lbl = (tbl.label || 'Detail') + (rows.length ? '   ·   showing ' + firstChunk.length + ' of ' + totalRows : '')
        sectionLabel(s, tableX, labelY, tableW, lbl)
        tableRows(s, tableX, firstRowsY, tableW, tbl.columns, firstChunk, tbl.hasPill)
      }
      if (noteOnFirst) noteCard(s, noteFirstX, noteFirstY, noteFirstW, CONTENT_BOTTOM - noteFirstY, sec.note)
    })

    const colW = 4.25
    const twoCol = tbl ? tableNeededWidth(tbl) <= colW + 0.15 : false
    const perCol = rowsThatFit(BODY_TOP + LABEL_H)
    const perSlide = twoCol ? perCol * 2 : perCol
    let shown = firstChunk.length
    while (rows.length) {
      const chunk = rows.splice(0, perSlide)
      const leftRows = chunk.slice(0, perCol)
      const rightRows = twoCol ? chunk.slice(perCol) : []
      shown += chunk.length
      const isLast = rows.length === 0
      let noteMode = null
      if (isLast && !notePlaced) {
        if (twoCol && rightRows.length <= Math.max(0, perCol - 4)) noteMode = 'rightCol'
        else {
          const usedRows = Math.max(leftRows.length, rightRows.length)
          if (CONTENT_BOTTOM - (BODY_TOP + LABEL_H + usedRows * ROW_H + 0.15) >= 1.0) noteMode = 'below'
        }
        if (noteMode) notePlaced = true
      }
      planSlide((s, p, t) => {
        wordmark(s, false); footer(s, p, t)
        titleBlock(s, sec.title, '(continued)')
        const lbl = (tbl.label || 'Detail') + ' — continued' + (rows.length ? '   ·   showing ' + shown + ' of ' + totalRows : '')
        sectionLabel(s, MARGIN, BODY_TOP, 9.0, lbl)
        const w = twoCol ? colW : 9.0
        tableRows(s, MARGIN, BODY_TOP + LABEL_H, w, tbl.columns, leftRows, tbl.hasPill)
        if (rightRows.length) tableRows(s, 5.25, BODY_TOP + LABEL_H, colW, tbl.columns, rightRows, tbl.hasPill)
        if (noteMode === 'rightCol') {
          const ny = BODY_TOP + LABEL_H + rightRows.length * ROW_H + (rightRows.length ? 0.15 : 0)
          noteCard(s, 5.25, ny, colW, Math.min(1.7, CONTENT_BOTTOM - ny), sec.note)
        } else if (noteMode === 'below') {
          const usedRows = Math.max(leftRows.length, rightRows.length)
          const ny = BODY_TOP + LABEL_H + usedRows * ROW_H + 0.15
          noteCard(s, MARGIN, ny, 9.0, Math.min(1.6, CONTENT_BOTTOM - ny), sec.note)
        }
      })
    }

    if (!notePlaced) {
      planSlide((s, p, t) => {
        wordmark(s, false); footer(s, p, t)
        titleBlock(s, sec.title, '(notes)')
        noteCard(s, MARGIN, BODY_TOP, 9.0, 1.6, sec.note)
      })
    }
  })

  if (D.decisions && D.decisions.length) {
    const items = D.decisions.slice()
    let firstPage = true
    while (items.length) {
      const chunk = items.splice(0, 4)
      const isFirst = firstPage
      firstPage = false
      planSlide((s, p, t) => {
        s.background = { color: TEAL }
        wordmark(s, true)
        s.addText('DECISIONS', { x: 0.55, y: 0.75, w: 5, h: 0.3, fontSize: 14, bold: true, color: YELLOW, charSpacing: 5, fontFace: F, margin: 0 })
        s.addText(isFirst ? (D.decisionsTitle || 'What we’re asking the board.') : 'Decisions, continued.', { x: 0.5, y: 1.1, w: 9, h: 0.7, fontSize: 34, bold: true, color: WHITE, fontFace: F, margin: 0 })
        chunk.forEach((d, i) => {
          const y = 2.05 + i * 0.72
          s.addShape(pres.ShapeType.rect, { x: 0.55, y: y + 0.05, w: 0.16, h: 0.16, fill: { color: YELLOW }, line: { color: YELLOW } })
          s.addText(d.title, { x: 0.9, y: y - 0.04, w: 3.4, h: 0.6, fontSize: 13, bold: true, color: WHITE, fontFace: F, valign: 'top', margin: 0 })
          s.addText(d.detail || '', { x: 4.4, y: y - 0.06, w: 5.1, h: 0.62, fontSize: 10.5, color: ICE, fontFace: F, valign: 'top', margin: 0 })
        })
        s.addText('Confidential — Board  ·  ' + p + ' / ' + t, { x: 6.5, y: 5.22, w: 3.0, h: 0.28, fontSize: 8, color: ICE, align: 'right', fontFace: F })
      })
    }
  }

  pres = new PptxGenJS()
  pres.defineLayout({ name: 'W', width: PAGE_W, height: PAGE_H })
  pres.layout = 'W'
  pres.author = 'PaySafeGo'
  pres.title = D.meta.deckName || `Board AML/CTF Report — ${D.meta.period}`
  pres.subject = 'Confidential board pack'
  const total = plan.length
  plan.forEach((fn, i) => fn(pres.addSlide(), i + 1, total))
  await pres.writeFile({ fileName: filename })
}
